import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NotificationService } from 'src/app/services/notification.service';
import { helper } from 'src/environments/helper';
import { InregistrareProgramareService } from '../inregistrare-programare.service';
import { finalize, map } from 'rxjs';
import { Entitate } from '../inregistrare-programare.interface';
import { NavigationService } from '../navigation.service';
import { SubcontractLocal, SubcontractLocalSlot } from '../disponibilitate-programare/disponibilitate.interface';
import Swal from 'sweetalert2';
import { ContractFileGroup } from '../../contracte-programari/contracte-programari.interface';

export interface ContractDraft {
    id: number;
    entity_id: number;
    date_from_contract: string;
    date_end_contract: string;
    nr_contract?: string;
    data_contract?: string;
    active: boolean;
    created_at: string;
    subcontracts: any[];
    entity: Entitate & { tutore_relationships?: any[] };
    file_groups: ContractFileGroup[];
}

@Component({
    selector: 'app-initialize-programare',
    templateUrl: './initialize-programare.component.html',
    styleUrls: ['./initialize-programare.component.scss']
})
export class InitializeProgramareComponent implements OnInit {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    helper = helper;

    mode: 'initializare' | 'drafturi' = 'initializare';

    isLoading = false;
    entitati: Entitate[] = [];
    drafturi: ContractDraft[] = [];
    drafturiLoaded = false;

    entitateForm: FormGroup = new FormGroup({
        name: new FormControl(''),
        cnp: new FormControl(''),
        nr_dosar: new FormControl(''),
    });

    constructor(
        private inregistrareProgramareService: InregistrareProgramareService,
        private navigationService: NavigationService,
        private notificationService: NotificationService,
    ) { }

    ngOnInit(): void {
        this.navigationService.currentStepIndex = 0;

        const savedForm = this.inregistrareProgramareService.getSearchForm();
        const savedEntitati = this.inregistrareProgramareService.getEntitati();
        const savedDrafturi = this.inregistrareProgramareService.getDrafturi();
        const savedMode = this.inregistrareProgramareService.getActiveMode();

        this.entitateForm.patchValue(savedForm);
        this.entitati = savedEntitati;
        this.drafturi = savedDrafturi;
        this.mode = savedMode;

        if (this.mode === 'drafturi' && this.drafturi.length > 0) {
            this.drafturiLoaded = true;
        }
    }

    setMode(m: 'initializare' | 'drafturi'): void {
        this.mode = m;
        this.inregistrareProgramareService.setActiveMode(m);
        this.drafturi = [];
        this.drafturiLoaded = false;
        this.entitati = [];
        this.inregistrareProgramareService.setDrafturi([]);
        this.inregistrareProgramareService.setEntitati([]);
    }

    // ── Inițializare ──────────────────────────────────────────────────────────

    findEntitate() {
        this.isLoading = true;
        this.inregistrareProgramareService.setSearchForm(this.entitateForm.value);

        this.inregistrareProgramareService
            .findEntitate(this.entitateForm.value)
            .pipe(
                map((results: any) => [...results.data]),
                finalize(() => { this.isLoading = false; })
            )
            .subscribe({
                next: (results) => {
                    this.entitati = results;
                    this.inregistrareProgramareService.setEntitati(results);
                },
                error: (err) => {
                    let msg;
                    try { msg = err.error.errors.message[0]; }
                    catch { msg = helper.dialogConfig.generalMessages.error; }
                    this.notificationService.warningSwal(this.errorTitle, msg, this.errorIcon);
                },
            });
    }

    selectEntitate(entitate: Entitate) {
        // Verificăm dacă entitatea are deja contracte active sau inactive
        const contracts: any[] = (entitate as any).contracts ?? [];
        if (contracts.length > 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Contract existent',
                html: `Beneficiarul <strong>${entitate.full_name}</strong> are deja ${contracts.length} contract(e) înregistrat(e).`,
                confirmButtonText: 'Închide',
                confirmButtonColor: '#1976d2',
            });
            return;
        }

        this.inregistrareProgramareService.setSelectedEntitate(entitate);
        this.navigationService.goToStep(1);
    }

    // ── Drafturi ──────────────────────────────────────────────────────────────

    listDrafturi() {
        this.isLoading = true;
        this.drafturiLoaded = false;

        this.inregistrareProgramareService
            .listContracte(this.entitateForm.value)
            .pipe(
                map((results: any) => (results.data as ContractDraft[]).filter(c => !c.active)),
                finalize(() => { this.isLoading = false; })
            )
            .subscribe({
                next: (results) => {
                    this.drafturi = results;
                    this.drafturiLoaded = true;
                    this.inregistrareProgramareService.setDrafturi(results);
                },
                error: (err) => {
                    let msg;
                    try { msg = err.error.errors.message[0]; }
                    catch { msg = helper.dialogConfig.generalMessages.error; }
                    this.notificationService.warningSwal(this.errorTitle, msg, this.errorIcon);
                },
            });
    }

    selectDraft(draft: ContractDraft) {
        const entitate: Entitate = {
            ...draft.entity,
            tutore_relationships: draft.entity.tutore_relationships ?? [],
        };
        this.inregistrareProgramareService.setSelectedEntitateForDraft(entitate);

        const subcontracteLocale: SubcontractLocal[] = draft.subcontracts
            // .filter(s => !s.active)
            .map(sub => this.mapSubcontractToLocal(sub, draft));


        this.inregistrareProgramareService.saveDetaliiState({
            contractId: draft.id,
            selectedStartDate: new Date(draft.date_from_contract),
            selectedEndDate: new Date(draft.date_end_contract),
            selectedLuni: null,
            nrContract: draft.nr_contract ?? '',
            dataContract: draft.data_contract ?? '',
            subcontracteLocale,
            disponibilitateActiva: false,
            sloturiSelectateIds: [],
            fileGroups: draft.file_groups ?? [],
        });

        this.navigationService.goToStep(1);
    }

    private mapSubcontractToLocal(sub: any, draft: ContractDraft): SubcontractLocal {
        const timeSlots: SubcontractLocalSlot[] = (sub.programari ?? []).map((p: any) => ({
            slot_id: p.slot_id,
            date: p.date,
            time_from: p.time_from,
            nume_specialist: p.program_disponibil?.nume_specialist ?? '',
            specialist_id: p.program_disponibil?.specialist_id ?? 0,
        }));

        // preferred_timeslots vine ca array de obiecte { day, hour_start, hour_end }
        const preferinteOrare: { day: number; hour_start: string; hour_end: string }[] =
            sub.preferred_timeslots ?? [];

        const noPreferinta = sub.preferred_timeslots === null;

        return {
            subcontract_id: sub.id,
            specializare_id: sub.specializare_id,
            specializare_name: sub.specializare_name
                ?? sub.denumire_specializare
                ?? sub.programari?.[0]?.program_disponibil?.denumire_specializare  // din prima programare
                ?? '',
            nr_sedinte: sub.nr_sedinte,
            frecventa_sedinte: sub.frecventa_sedinte,
            date_from_sedinte: sub.date_from_sedinte,
            date_end_sedinte: sub.date_end_sedinte,
            timeSlots,
            nr_contract: draft.nr_contract ?? '',
            data_contract: draft.data_contract ?? '',
            date_from_contract: draft.date_from_contract,
            date_end_contract: draft.date_end_contract,
            preferinteOrare,
            preluareContract: false,
            noPreferinta,
        };
    }
}